import type { ClientDTO, ClientDetailDTO, ContactDTO, KnownSentimentLevel, SentimentLevel } from '@copilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { clientsApi } from '../../api/clients';
import { useUser } from '../../auth/AuthProvider';
import {
  Avatar,
  Button,
  Card,
  ClientPicker,
  ConfirmDialog,
  CountUp,
  EmptyState,
  ErrorBanner,
  Input,
  PageSkeleton,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
} from '../../components';
import { cn } from '../../lib/cn';
import { formatDate, formatDateTime, formatDuration, formatNumber } from '../../lib/format';
import { SENTIMENT_LABELS } from '../../lib/labels';
import { OutcomeBadge, SentimentBadge } from '../meetings/badges';
import { ClientForm, toClientBody } from './ClientForm';
import { SentimentTrend } from './SentimentTrend';

export function ClientDetailPage() {
  const { id = '' } = useParams();
  const user = useUser();
  const query = useQuery({ queryKey: ['client', id], queryFn: () => clientsApi.get(id) });

  if (query.isLoading) return <PageSkeleton />;
  if (query.isError)
    return (
      <div className="space-y-3">
        <BackLink />
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  const c = query.data;
  if (!c) return null;
  const today = new Date().toISOString().slice(0, 10);
  const overdue = c.openNextSteps.filter((step) => step.dueDate && step.dueDate < today).length;

  return (
    <div className="space-y-4">
      <BackLink />
      <div className="flex items-center gap-3">
        <Avatar name={c.name} size="lg" />
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-ink">{c.name}</h1>
          <p className="mt-0.5 text-sm text-muted">
            {[c.industry, c.ownerName && `Owner: ${c.ownerName}`].filter(Boolean).join(' · ') || 'No details yet'}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Client sentiment"
          value={c.sentiment ? <span className={sentimentText(c.sentiment.level)}>{SENTIMENT_LABELS[c.sentiment.level]}</span> : '—'}
          sub={
            c.sentiment
              ? `average of the last ${c.sentiment.basedOn} ${c.sentiment.basedOn === 1 ? 'meeting' : 'meetings'}`
              : 'appears when a meeting is summarised'
          }
        />
        <div className="rise lift rounded-card border border-line bg-surface px-5 py-4 shadow-card" style={{ '--i': 1 } as CSSProperties}>
          <div className="text-xs text-muted">Sentiment trend</div>
          <div className="mt-2">
            {c.sentiment ? <SentimentTrend history={c.sentiment.history} /> : <p className="text-sm text-muted">No sentiment yet.</p>}
          </div>
        </div>
        <StatTile
          style={{ '--i': 2 } as CSSProperties}
          label="Meetings"
          value={<CountUp value={c.meetingCount} />}
          sub={c.lastMeetingAt ? `last met ${formatDate(c.lastMeetingAt)}` : 'no meetings yet'}
        />
        <StatTile
          style={{ '--i': 3 } as CSSProperties}
          label="Open next steps"
          value={<CountUp value={c.openNextSteps.length} />}
          sub={overdue > 0 ? `${overdue} overdue` : 'none overdue'}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0 space-y-4">
          <MeetingsTimeline client={c} />
          <ContactsCard client={c} />
        </div>
        <div className="min-w-0 space-y-4">
          <EditCard key={c.id} client={c} />
          <NextStepsCard client={c} />
          {user.role === 'admin' && <DangerZone client={c} />}
        </div>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/clients" className="text-xs text-muted hover:text-ink">
      ← Back to clients
    </Link>
  );
}

function useInvalidateClient(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['client', id] });
    void queryClient.invalidateQueries({ queryKey: ['clients'] });
  };
}

function EditCard({ client }: { client: ClientDetailDTO }) {
  const invalidate = useInvalidateClient(client.id);
  const save = useMutation({
    mutationFn: (body: ReturnType<typeof toClientBody>) => clientsApi.patch(client.id, body),
    onSuccess: invalidate,
  });
  return (
    <Card title="Client details">
      <ClientForm
        initial={{ name: client.name, industry: client.industry ?? '', domains: client.domains, notes: client.notes ?? '' }}
        submitLabel="Save changes"
        busy={save.isPending}
        error={save.error}
        requireDirty
        onSubmit={(v) => save.mutate(toClientBody(v))}
      />
    </Card>
  );
}

function sentimentText(level: KnownSentimentLevel): string {
  if (level === 'positive' || level === 'very_positive') return 'text-good';
  if (level === 'negative' || level === 'very_negative') return 'text-danger';
  return 'text-ink';
}

/** Timeline marker: negative meetings are squares, so it does not rely on color alone. */
function markerClass(level: SentimentLevel | null): string {
  if (level === 'positive' || level === 'very_positive') return 'rounded-full bg-good';
  if (level === 'negative' || level === 'very_negative') return 'rounded-[2px] bg-danger';
  return 'rounded-full bg-line-strong';
}

function MeetingsTimeline({ client }: { client: ClientDetailDTO }) {
  const meetings = [...client.meetings].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  return (
    <Card title="Meetings" description="All meetings with this client, newest first.">
      {meetings.length === 0 ? (
        <EmptyState title="No meetings with this client yet" />
      ) : (
        <ol className="relative space-y-4 border-l border-line pl-4">
          {meetings.map((m) => (
            <li key={m.id} className="relative">
              <span className={cn('absolute top-1.5 -left-[21px] h-2.5 w-2.5 border-2 border-surface', markerClass(m.sentiment))} aria-hidden />
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                <span>{formatDateTime(m.startedAt)}</span>
                <span>·</span>
                <span>{m.repName}</span>
                <span>·</span>
                <span>{formatDuration(m.durationSec)}</span>
                <OutcomeBadge outcome={m.outcome} />
                <SentimentBadge level={m.sentiment} />
              </div>
              <Link to={`/meetings/${m.id}`} className="text-sm font-medium text-ink hover:text-accent-ink hover:underline">
                {m.title || 'Untitled meeting'}
              </Link>
              {m.headline && <p className="text-sm text-ink-2">{m.headline}</p>}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function NextStepsCard({ client }: { client: ClientDetailDTO }) {
  return (
    <Card title="Open next steps" description="Collected from all meetings with this client.">
      {client.openNextSteps.length === 0 ? (
        <p className="text-sm text-muted">No open next steps.</p>
      ) : (
        <ul className="space-y-2">
          {client.openNextSteps.map((n, i) => (
            <li key={`${n.meetingId}-${i}`} className="text-sm">
              <div className="text-ink">{n.text}</div>
              <div className="text-xs text-muted">
                {[n.owner, n.dueDate && `due ${formatDate(n.dueDate)}`].filter(Boolean).join(' · ')}
                {(n.owner || n.dueDate) && ' · '}
                <Link to={`/meetings/${n.meetingId}`} className="hover:underline">
                  from {formatDate(n.meetingDate)}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

interface ContactDraft {
  name: string;
  email: string;
  title: string;
}

const emptyDraft: ContactDraft = { name: '', email: '', title: '' };

function draftToBody(d: ContactDraft) {
  return { name: d.name.trim(), email: d.email.trim() || null, title: d.title.trim() || null };
}

function ContactsCard({ client }: { client: ClientDetailDTO }) {
  const invalidate = useInvalidateClient(client.id);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(emptyDraft);
  const [adding, setAdding] = useState<ContactDraft>(emptyDraft);
  const [deleting, setDeleting] = useState<ContactDTO | null>(null);

  const add = useMutation({
    mutationFn: () => clientsApi.addContact(client.id, draftToBody(adding)),
    onSuccess: () => {
      setAdding(emptyDraft);
      invalidate();
    },
  });
  const update = useMutation({
    mutationFn: (id: string) => clientsApi.patchContact(id, draftToBody(draft)),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => clientsApi.deleteContact(id),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
  });

  const draftInputs = (d: ContactDraft, set: (d: ContactDraft) => void) => (
    <>
      <Td>
        <Input aria-label="Name" value={d.name} onChange={(e) => set({ ...d, name: e.target.value })} placeholder="Name" />
      </Td>
      <Td>
        <Input aria-label="Email" type="email" value={d.email} onChange={(e) => set({ ...d, email: e.target.value })} placeholder="name@company.com" />
      </Td>
      <Td>
        <Input aria-label="Title" value={d.title} onChange={(e) => set({ ...d, title: e.target.value })} placeholder="Job title" />
      </Td>
    </>
  );

  return (
    <Card title="Contacts" bodyClassName="p-0">
      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Title</Th>
            <Th className="w-36" />
          </tr>
        </thead>
        <tbody>
          {client.contacts.map((c) =>
            editingId === c.id ? (
              <tr key={c.id}>
                {draftInputs(draft, setDraft)}
                <Td className="whitespace-nowrap">
                  <Button size="sm" variant="primary" loading={update.isPending} onClick={() => update.mutate(c.id)}>
                    Save
                  </Button>{' '}
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </Td>
              </tr>
            ) : (
              <Tr key={c.id}>
                <Td>{c.name || <span className="text-muted">—</span>}</Td>
                <Td className="text-ink-2">{c.email ?? '—'}</Td>
                <Td className="text-ink-2">{c.title ?? '—'}</Td>
                <Td className="whitespace-nowrap text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      update.reset();
                      setEditingId(c.id);
                      setDraft({ name: c.name, email: c.email ?? '', title: c.title ?? '' });
                    }}
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDeleting(c)}>
                    Delete
                  </Button>
                </Td>
              </Tr>
            ),
          )}
          <tr>
            {draftInputs(adding, setAdding)}
            <Td>
              <Button
                size="sm"
                loading={add.isPending}
                disabled={!adding.name.trim() && !adding.email.trim()}
                onClick={() => add.mutate()}
              >
                Add contact
              </Button>
            </Td>
          </tr>
        </tbody>
      </Table>
      <ErrorBanner error={add.error ?? update.error} className="m-3" />
      <ConfirmDialog
        open={!!deleting}
        title="Delete contact"
        danger
        confirmLabel="Delete"
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onCancel={() => {
          setDeleting(null);
          remove.reset();
        }}
      >
        Remove {deleting?.name || deleting?.email} from {client.name}? Meetings they attended are kept.
      </ConfirmDialog>
    </Card>
  );
}

function DangerZone({ client }: { client: ClientDetailDTO }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateClient(client.id);
  const [source, setSource] = useState<ClientDTO | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const merge = useMutation({
    mutationFn: (sourceId: string) => clientsApi.merge(client.id, sourceId),
    onSuccess: () => {
      setSource(null);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
  const deleteData = useMutation({
    mutationFn: () => clientsApi.deleteAllData(client.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.removeQueries({ queryKey: ['client', client.id] });
      void navigate('/clients', { replace: true });
    },
  });

  return (
    <Card title="Admin actions" className="border-danger/40">
      <div className="space-y-5">
        <div>
          <h3 className="text-sm font-medium text-ink">Merge a duplicate into this client</h3>
          <p className="mb-2 text-xs text-muted">
            Moves the duplicate's meetings and contacts into {client.name}, then deletes the duplicate.
          </p>
          <ClientPicker excludeId={client.id} onSelect={setSource} placeholder="Find the duplicate client…" />
          {merge.isSuccess && <p className="mt-2 text-xs text-good">Merged.</p>}
        </div>
        <div>
          <h3 className="text-sm font-medium text-ink">Delete all data for this client</h3>
          <p className="mb-2 text-xs text-muted">
            For data-deletion requests: removes the client and all of its meetings, transcripts and contacts. Audit-logged.
          </p>
          <Button variant="danger" onClick={() => setConfirmDelete(true)}>
            Delete all client data
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={!!source}
        title="Merge clients"
        confirmLabel="Merge"
        loading={merge.isPending}
        error={merge.error}
        onConfirm={() => source && merge.mutate(source.id)}
        onCancel={() => {
          setSource(null);
          merge.reset();
        }}
      >
        Move all {source ? formatNumber(source.meetingCount) : ''} meetings and the contacts of <strong>{source?.name}</strong> into{' '}
        <strong>{client.name}</strong> and delete <strong>{source?.name}</strong>?
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmDelete}
        title="Delete all client data"
        danger
        requireText={client.name}
        confirmLabel="Delete everything"
        loading={deleteData.isPending}
        error={deleteData.error}
        onConfirm={() => deleteData.mutate()}
        onCancel={() => {
          setConfirmDelete(false);
          deleteData.reset();
        }}
      >
        This permanently deletes <strong>{client.name}</strong>, {formatNumber(client.meetingCount)} meetings with their transcripts and
        summaries, and {formatNumber(client.contacts.length)} contacts. It cannot be undone.
      </ConfirmDialog>
    </Card>
  );
}
